import { useEffect, useState } from "react";
import { uploadUrl } from "@/services/config";
import type { MediaItemPatch, MediaLibraryFolder, MediaLibraryItem } from "@/types";
import { dimensionLabel, fileKind, formatMediaDate, isImage } from "../utils";

interface MediaDetailsPanelProps {
  item: MediaLibraryItem | null;
  total: number;
  sizeLabel: string;
  folders: MediaLibraryFolder[];
  saving: boolean;
  deleting: boolean;
  onSave: (patch: MediaItemPatch) => void;
  onDelete: () => void;
}

/** A read-only metadata line (label on the left, value on the right). */
function MetaRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-start justify-between gap-3 py-1.5">
      <span className="text-xs font-medium text-foreground-500">{label}</span>
      <span className="text-right text-xs font-semibold text-foreground-800 break-all">{value}</span>
    </div>
  );
}

export default function MediaDetailsPanel({
  item,
  total,
  sizeLabel,
  folders,
  saving,
  deleting,
  onSave,
  onDelete,
}: MediaDetailsPanelProps) {
  const [title, setTitle] = useState("");
  const [altText, setAltText] = useState("");
  const [caption, setCaption] = useState("");
  const [copied, setCopied] = useState(false);

  // Re-seed the editable fields whenever a different item is opened.
  useEffect(() => {
    setTitle(item?.title || "");
    setAltText(item?.altText || "");
    setCaption(item?.caption || "");
    setCopied(false);
  }, [item?.id]);

  if (!item) {
    return (
      <div className="rounded-lg border border-background-200 bg-background-50 p-5">
        <h2 className="font-heading text-sm font-bold text-foreground-950">Library</h2>
        <p className="mt-1 text-xs text-foreground-500">Select an item to edit its details.</p>
        <div className="mt-4 grid grid-cols-2 gap-3">
          <div className="rounded-md border border-background-200 bg-background-100/60 p-3">
            <p className="text-[11px] uppercase tracking-wide text-foreground-500">Files</p>
            <p className="mt-1 font-heading text-xl font-extrabold text-foreground-950">{total}</p>
          </div>
          <div className="rounded-md border border-background-200 bg-background-100/60 p-3">
            <p className="text-[11px] uppercase tracking-wide text-foreground-500">Used</p>
            <p className="mt-1 font-heading text-xl font-extrabold text-foreground-950">{sizeLabel}</p>
          </div>
        </div>

        <h3 className="mt-5 text-[11px] font-bold uppercase tracking-wider text-foreground-500">Folders</h3>
        <ul className="mt-2 space-y-1">
          {folders.map((folder) => (
            <li
              key={folder.folder}
              className="flex items-center justify-between rounded-md px-2.5 py-2 text-sm text-foreground-700 hover:bg-background-100"
            >
              <span className="flex items-center gap-2">
                <i className="ri-folder-line text-base text-foreground-400"></i>
                {folder.folder}
              </span>
              <span className="text-xs text-foreground-400">{folder.count}</span>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const previewSrc = uploadUrl(item.url);

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(previewSrc);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };

  const dirty = title !== (item.title || "") || altText !== (item.altText || "") || caption !== (item.caption || "");

  return (
    <div className="rounded-lg border border-background-200 bg-background-50 p-5">
      <h2 className="font-heading text-sm font-bold text-foreground-950">Attachment details</h2>

      <div className="mt-3 flex items-center justify-center overflow-hidden rounded-lg border border-background-200 bg-background-100/60 p-2">
        {isImage(item) ? (
          <img src={previewSrc} alt={item.altText || item.title || item.originalName} className="max-h-56 w-full object-contain" />
        ) : (
          <div className="flex h-40 w-full items-center justify-center text-foreground-400">
            <i className="ri-file-line text-4xl"></i>
          </div>
        )}
      </div>

      <label htmlFor="media-title" className="mt-4 mb-1 block text-[11px] font-bold uppercase tracking-wider text-foreground-500">
        Title
      </label>
      <input
        id="media-title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
      />

      <label htmlFor="media-alt" className="mt-3 mb-1 block text-[11px] font-bold uppercase tracking-wider text-foreground-500">
        Alt text
      </label>
      <input
        id="media-alt"
        value={altText}
        onChange={(e) => setAltText(e.target.value)}
        placeholder="Describe the image for SEO…"
        className="w-full rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
      />

      <label htmlFor="media-caption" className="mt-3 mb-1 block text-[11px] font-bold uppercase tracking-wider text-foreground-500">
        Caption
      </label>
      <textarea
        id="media-caption"
        value={caption}
        onChange={(e) => setCaption(e.target.value)}
        rows={2}
        maxLength={500}
        className="w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 focus:border-primary-400 focus:outline-none"
      ></textarea>

      <button
        type="button"
        onClick={() => onSave({ title, altText, caption })}
        disabled={saving || !dirty}
        className="mt-3 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
      >
        <i className={saving ? "ri-loader-4-line animate-spin" : "ri-save-line"}></i>
        {saving ? "Saving…" : "Save details"}
      </button>

      <div className="mt-4 border-t border-background-100 pt-2">
        <MetaRow label="File name" value={item.fileName} />
        <MetaRow label="File type" value={`${fileKind(item)}${item.mime ? ` · ${item.mime}` : ""}`} />
        <MetaRow label="Dimensions" value={dimensionLabel(item)} />
        <MetaRow label="File size" value={item.size} />
        <MetaRow label="Folder" value={item.folder} />
        <MetaRow label="Uploaded by" value={item.uploader} />
        <MetaRow label="Uploaded on" value={formatMediaDate(item.createdAt)} />
      </div>

      <div className="mt-3">
        <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-foreground-500">File URL</label>
        <div className="flex items-center gap-2">
          <input
            readOnly
            value={previewSrc}
            onFocus={(e) => e.currentTarget.select()}
            className="w-full rounded-md border border-background-300 bg-background-100/60 px-3 py-2 text-xs text-foreground-600 focus:outline-none"
          />
          <button
            type="button"
            onClick={copyUrl}
            aria-label="Copy URL"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 cursor-pointer"
          >
            <i className={copied ? "ri-check-line text-accent-600" : "ri-file-copy-line"}></i>
          </button>
        </div>
      </div>

      <button
        type="button"
        onClick={onDelete}
        disabled={deleting}
        className="mt-4 inline-flex w-full items-center justify-center gap-2 rounded-md border border-secondary-200 px-4 py-2.5 text-sm font-semibold text-secondary-700 hover:bg-secondary-50 disabled:cursor-not-allowed disabled:opacity-50 whitespace-nowrap cursor-pointer"
      >
        <i className={deleting ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}></i>
        {deleting ? "Deleting…" : "Delete permanently"}
      </button>
    </div>
  );
}